import {
  headingAnchors,
  headingFragment,
  MODEL_ROLES,
  type ProjectDetail,
  type WikiGenerated,
  type WikiPageView,
} from "@esi/shared";
import { BookOpen, PanelLeft, PanelRight, Plus } from "lucide-react";
import { type CSSProperties, type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useMediaQuery } from "@/hooks/use-media-query";
import { formatDateTime } from "../i18n/format.ts";
import { t as translate } from "../i18n/i18n.ts";
import { KnowledgeTree, useFolderParam } from "../projects/KnowledgeTree.tsx";
import { Link, pageHistoryPath, useSearch, wikiPath } from "../router.tsx";
import { CompanionSlotsContext } from "./PagePassages.tsx";
import { PageStewardship } from "./PageStewardship.tsx";
import { PagesSplitter, usePagesWidth } from "./PagesSplitter.tsx";
import { PageDiscussion } from "./PageThreads.tsx";
import { useWikiPage } from "./queries.ts";

/** Fixed chrome with three independently scrolling panels; restricted content stays in memory. */
export function WikiDocs(props: {
  project: ProjectDetail;
  pageId: string | null;
  canWrite: boolean;
  onNew: () => void;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const wide = useMediaQuery("(min-width: 1024px)");
  const railWide = useMediaQuery("(min-width: 1280px)");
  const [folder, setFolder] = useFolderParam();
  const [treeOpen, setTreeOpen] = useState(false);
  const [railOpen, setRailOpen] = useState(false);
  const [side, setSide] = useState<HTMLElement | null>(null);
  const [bottom, setBottom] = useState<HTMLElement | null>(null);
  const [paneOpen, setPaneOpen] = useState(false);
  const slots = useMemo(() => ({ side, bottom, setPaneOpen }), [side, bottom]);
  const pages = usePagesWidth();
  const treeTrigger = useRef<HTMLButtonElement>(null);
  const railTrigger = useRef<HTMLButtonElement>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: close the sheet after page navigation
  useEffect(() => {
    setTreeOpen(false);
    // Back to the overview: no rail, so its sheet must not reopen on the next page.
    if (props.pageId === null) {
      setRailOpen(false);
    }
  }, [props.pageId]);
  const version = Number(useSearch().get("version")) || undefined;
  const page = useWikiPage(props.project.id, props.pageId ?? "", version, !!props.pageId);
  const tree = (
    <div className="flex min-h-0 flex-1 flex-col bg-surface-rail">
      {wide ? (
        <div className="flex h-bar shrink-0 items-center justify-between px-4">
          <h2 className="text-sm font-semibold text-fg">{t("wiki.docs.pages")}</h2>
          {props.canWrite ? (
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={t("wiki.newPage")}
              onClick={props.onNew}
            >
              <Plus aria-hidden="true" />
            </Button>
          ) : null}
        </div>
      ) : null}
      <KnowledgeTree
        project={props.project}
        scope="wiki"
        narrow={!wide}
        folderParam={folder}
        onFolderParam={(id) => {
          setFolder(id);
        }}
        upload={null}
        toolbar={
          !wide && props.canWrite ? (
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={t("wiki.newPage")}
              onClick={props.onNew}
            >
              <Plus aria-hidden="true" />
            </Button>
          ) : null
        }
        selectedId={props.pageId ? `w:${props.pageId}` : null}
        onRootChange={() => {}}
        // The wiki's own page tree: no "Open the wiki" link to the page it is on, and no
        // sources (#252); the centre says what to do next.
        empty={
          <EmptyState icon={BookOpen} headingLevel={3} title={t("wiki.docs.empty.title")}>
            {t("wiki.docs.empty.body")}
          </EmptyState>
        }
      />
    </div>
  );
  // Page metadata only beside a page: the overview has no rail and no Details trigger. The page's
  // companion pane (wiki-collaboration-ux S4) takes the rail's place while a thread is open.
  const hasRail = props.pageId !== null && !paneOpen;
  const rail = page.data ? <PageRail project={props.project} page={page.data} /> : null;
  return (
    <div data-esi-content="wiki" className="flex min-h-0 flex-1 flex-col">
      {!wide || (hasRail && !railWide) ? (
        <div className="flex shrink-0 items-center justify-between border-b border-line px-3 py-1">
          {!wide ? (
            <Button ref={treeTrigger} variant="ghost" size="sm" onClick={() => setTreeOpen(true)}>
              <PanelLeft aria-hidden="true" />
              {t("wiki.docs.pages")}
            </Button>
          ) : (
            <span />
          )}
          {hasRail && !railWide ? (
            <Button ref={railTrigger} variant="ghost" size="sm" onClick={() => setRailOpen(true)}>
              <PanelRight aria-hidden="true" />
              {t("wiki.docs.details")}
            </Button>
          ) : null}
        </div>
      ) : null}
      <div className="flex min-h-0 flex-1">
        {wide ? (
          <>
            <aside
              aria-label={t("wiki.docs.pages")}
              // The chosen width, capped live at a quarter of the window (#418).
              className="flex w-[max(200px,min(var(--pages-width),25vw))] shrink-0 flex-col overflow-hidden"
              style={{ "--pages-width": `${pages.width}px` } as CSSProperties}
            >
              {tree}
            </aside>
            <PagesSplitter width={pages.width} onWidth={pages.set} onReset={pages.reset} />
          </>
        ) : null}
        <div className="flex min-w-0 flex-1 flex-col">
          <div
            data-scroll-owner="page"
            // Wide blocks on a page may widen past the reading column (#379).
            className="wide-host min-h-0 flex-1 overflow-y-auto overscroll-contain"
          >
            <div className="mx-auto w-full max-w-[76ch] px-5 py-7 sm:px-8 lg:py-9">
              <CompanionSlotsContext.Provider value={slots}>
                {props.children}
              </CompanionSlotsContext.Provider>
            </div>
          </div>
          {/* The page thread's bottom sheet (below 1024 px, or docked to the bottom). */}
          <div ref={setBottom} className="contents" />
        </div>
        {/* The page thread's pane, in the rail's place while it is open. */}
        <div ref={setSide} className="contents" />
        {hasRail && railWide ? (
          <aside
            aria-label={t("wiki.docs.details")}
            className="w-64 shrink-0 overflow-y-auto border-l border-line"
          >
            {rail}
          </aside>
        ) : null}
      </div>
      <Sheet open={!wide && treeOpen} onOpenChange={setTreeOpen}>
        <SheetContent
          side="left"
          className="w-[min(22rem,100vw)] gap-0 p-0"
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            treeTrigger.current?.focus();
          }}
        >
          <SheetHeader className="border-b border-line">
            <SheetTitle>{t("wiki.docs.pages")}</SheetTitle>
            <SheetDescription className="sr-only">{props.project.name}</SheetDescription>
          </SheetHeader>
          <div className="flex min-h-0 flex-1 flex-col">{tree}</div>
        </SheetContent>
      </Sheet>
      <Sheet open={hasRail && !railWide && railOpen} onOpenChange={setRailOpen}>
        <SheetContent
          side="right"
          className="w-[min(22rem,100vw)] gap-0 overflow-y-auto p-0"
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            railTrigger.current?.focus();
          }}
        >
          <SheetHeader className="border-b border-line">
            <SheetTitle>{t("wiki.docs.details")}</SheetTitle>
            <SheetDescription>{page.data?.title ?? props.project.name}</SheetDescription>
          </SheetHeader>
          {rail}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function PageRail({ project, page }: { project: ProjectDetail; page: WikiPageView }) {
  const projectId = project.id;
  const { t } = useTranslation();
  const headings = headingAnchors(page.markdown);
  return (
    <div className="flex flex-col gap-7 p-5 text-xs">
      <section className="flex flex-col gap-3">
        <h2 className="text-xs font-semibold text-fg">{t("wiki.docs.about")}</h2>
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 text-fg-secondary">
          <dt>{t("wiki.editor.pageType")}</dt>
          <dd className="text-right text-fg">{t(`wiki.pageTypes.${page.pageType}`)}</dd>
          <dt>{t("wiki.docs.status")}</dt>
          <dd className="text-right text-fg">
            {t(page.status === "processing" ? "wiki.processing" : "wiki.docs.ready")}
          </dd>
          <dt>{t("wiki.docs.version")}</dt>
          <dd className="text-right text-fg tabular-nums">{page.shownVersion}</dd>
          {page.generated ? (
            <>
              <dt>{t("wiki.docs.writtenBy")}</dt>
              <dd className="text-right break-words text-fg">{writtenBy(page.generated)}</dd>
            </>
          ) : null}
          {page.trust ? (
            <>
              <dt>{t("wiki.docs.trust")}</dt>
              <dd className="text-right text-fg">{t(`wiki.docs.trustValues.${page.trust}`)}</dd>
            </>
          ) : null}
        </dl>
      </section>
      <PageStewardship projectId={projectId} page={page} />
      <PageDiscussion project={project} page={page} variant="rail" />
      {headings.length ? (
        <nav aria-label={t("wiki.docs.contents")} className="flex flex-col gap-2.5">
          <h2 className="text-xs font-semibold text-fg">{t("wiki.docs.contents")}</h2>
          {headings.map((heading) => (
            <a
              key={heading.block}
              href={`#wiki-section-${headingFragment(heading)}`}
              className="text-fg-secondary underline-offset-4 hover:text-fg hover:underline"
              onClick={(e) => {
                e.preventDefault();
                document
                  .getElementById(`wiki-section-${headingFragment(heading)}`)
                  ?.scrollIntoView({ block: "start" });
              }}
            >
              {heading.text}
            </a>
          ))}
        </nav>
      ) : null}
      <section className="flex flex-col gap-2.5">
        <h2 className="text-xs font-semibold text-fg">{t("wiki.backlinks")}</h2>
        {page.backlinks.length ? (
          page.backlinks.map((link) => (
            <Link
              key={link.id}
              href={wikiPath(projectId, link.id)}
              className="text-accent-text hover:underline"
            >
              {link.title}
            </Link>
          ))
        ) : (
          <p className="text-fg-secondary">{t("wiki.noBacklinks")}</p>
        )}
      </section>
      <section className="flex flex-col gap-2.5">
        <h2 className="text-xs font-semibold text-fg">{t("wiki.docs.history")}</h2>
        {page.versions.slice(0, 4).map((v) => (
          <Link
            key={v.version}
            href={wikiPath(projectId, page.id, v.version)}
            className="text-fg-secondary hover:text-fg hover:underline"
          >
            {t("wiki.version", { version: v.version })}
            <span className="mt-0.5 block text-fg-muted">{formatDateTime(v.publishedAt)}</span>
          </Link>
        ))}
        <Link
          href={pageHistoryPath(projectId, page.id)}
          className="text-accent-text hover:underline"
        >
          {t("wiki.docs.allHistory")}
        </Link>
      </section>
      {page.provenance ? (
        <section className="flex flex-col gap-2.5">
          <h2 className="text-xs font-semibold text-fg">{t("wiki.provenance")}</h2>
          {page.provenance.length === 0 ? (
            <p className="text-fg-secondary">{t("wiki.docs.noSources")}</p>
          ) : null}
          {page.provenance.map((source, i) => (
            <p key={source.id ?? i} className="break-words text-fg-secondary">
              {source.title ?? t("wiki.draft.sourceDeleted")}
            </p>
          ))}
        </section>
      ) : null}
    </div>
  );
}

/**
 * How the shown version was written, in words (spec `project-wiki` "Pages state how they were
 * written"): the agent with its role (never a model), a person, an MCP client by its host when
 * the viewer may see the change set, ingestion or a revert.
 */
export function writtenBy(generated: WikiGenerated): string {
  const role = MODEL_ROLES.find((r) => r === generated.modelRole);
  switch (generated.origin) {
    case "agent":
    case "answer":
      return role
        ? translate("wiki.docs.written.agentRole", {
            role: translate(`wiki.docs.written.roles.${role}`),
          })
        : translate(
            generated.origin === "agent" ? "wiki.docs.written.agent" : "wiki.docs.written.answer",
          );
    case "mcp":
      return generated.clientName
        ? translate("wiki.docs.written.mcpClient", { client: generated.clientName })
        : translate("wiki.docs.written.mcp");
    case "ingestion":
      return translate("wiki.docs.written.ingestion");
    case "revert":
      return translate("wiki.docs.written.revert");
    default:
      return translate("wiki.docs.written.person");
  }
}
